import React, { useRef, useState } from 'react';
import { Upload, Loader2, X, Image as ImageIcon, Crop } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import ImageCropperModal from './ImageCropperModal';

/**
 * Image uploader with an integrated crop modal.
 *
 *   Flow:
 *     1. User picks a file → we read it as a data URL.
 *     2. ImageCropperModal opens — they pan / zoom / rotate.
 *     3. They confirm → we get a JPEG Blob and POST it as /api/upload-image.
 *     4. `onChange(url)` fires with the public URL of the cropped image.
 *
 * Props:
 *   - shape: 'circle' for round preview (default in interpreter forms).
 *   - cropAspect: aspect ratio for the cropper (default 1:1 square).
 *   - skipCrop: bypass the cropper and upload as-is.
 */
export default function ImageUploader({
  value, onChange, testid, label, hint,
  shape = 'rect', cropAspect = 1, skipCrop = false,
  uploadEndpoint = '/upload-image', uploadExtra = null,
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [pendingFile, setPendingFile] = useState(null);  // { dataUrl, name }

  function readAsDataURL(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async function onFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setError('');
    if (skipCrop) {
      await uploadFile(f, f.name);
    } else {
      try {
        const dataUrl = await readAsDataURL(f);
        setPendingFile({ dataUrl, name: f.name });
      } catch (err) {
        setError('No se pudo leer la imagen.');
      }
    }
    if (fileRef.current) fileRef.current.value = '';
  }

  async function uploadFile(blob, filename) {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', blob, filename || 'image.jpg');
      if (uploadExtra) {
        Object.entries(uploadExtra).forEach(([k, v]) => fd.append(k, v));
      }
      const { data } = await api.post(uploadEndpoint, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(data.url);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Error subiendo imagen');
    } finally {
      setUploading(false);
    }
  }

  async function handleCropConfirm(blob) {
    setPendingFile(null);
    await uploadFile(blob, pendingFile?.name || 'cropped.jpg');
  }

  const previewClass = shape === 'circle'
    ? 'w-20 h-20 rounded-full object-cover object-top border-2 border-gray-200'
    : 'w-32 h-20 rounded-lg object-cover object-top border-2 border-gray-200';

  const emptyClass = shape === 'circle'
    ? 'w-20 h-20 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center text-charcoal-400'
    : 'w-32 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-charcoal-400';

  return (
    <div>
      {label && <label className="block text-xs font-medium text-charcoal-600 mb-1.5">{label}</label>}
      <div className="flex items-center gap-3 flex-wrap">
        {value ? (
          <div className="relative">
            <img src={value} alt="" className={previewClass} />
            <button
              type="button"
              onClick={() => onChange('')}
              title="Quitar"
              className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white border border-gray-200 text-charcoal-500 hover:text-rose-600 flex items-center justify-center shadow-sm"
            >
              <X size={11} />
            </button>
          </div>
        ) : (
          <div className={emptyClass}><ImageIcon size={20} /></div>
        )}
        <div className="flex flex-col gap-1">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={onFile}
            data-testid={`${testid || 'image-uploader'}-input`}
            className="hidden"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid={`${testid || 'image-uploader'}-btn`}
              className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-50 disabled:opacity-60"
            >
              {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
              {uploading ? 'Subiendo…' : value ? 'Reemplazar' : 'Subir imagen'}
            </button>
            {value && !skipCrop && (
              <button
                type="button"
                onClick={async () => {
                  // Re-open cropper using the existing image (must be CORS-accessible)
                  try {
                    const r = await fetch(value, { mode: 'cors' });
                    const blob = await r.blob();
                    const dataUrl = await new Promise((resolve, reject) => {
                      const reader = new FileReader();
                      reader.onload = () => resolve(reader.result);
                      reader.onerror = reject;
                      reader.readAsDataURL(blob);
                    });
                    setPendingFile({ dataUrl, name: 'edited.jpg' });
                  } catch {
                    setError('No se puede recortar imágenes externas (CORS). Sube de nuevo.');
                  }
                }}
                data-testid={`${testid || 'image-uploader'}-recrop-btn`}
                className="inline-flex items-center gap-1.5 bg-cmic-50 border border-cmic-200 text-cmic-700 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-cmic-100"
                title="Volver a recortar"
              >
                <Crop size={12} />Recortar
              </button>
            )}
          </div>
          <span className="text-[10px] text-charcoal-400">{hint || 'JPG, PNG, WebP — máx 5 MB. Te dejaré recortar antes de guardar.'}</span>
        </div>
      </div>
      {error && <div className="text-xs text-rose-700 mt-1">{error}</div>}

      {pendingFile && (
        <ImageCropperModal
          srcDataUrl={pendingFile.dataUrl}
          aspect={cropAspect}
          shape={shape === 'circle' ? 'round' : 'rect'}
          onConfirm={handleCropConfirm}
          onCancel={() => setPendingFile(null)}
        />
      )}
    </div>
  );
}
